/**
 * React Query hooks for the Amazon Business buyer integration (ROADMAP §1.7,
 * migration 61). Mirrors the patterns in `features/integrations/hooks.ts` and
 * `features/financials/hooks.ts`:
 *
 *   - `useAmazonBusinessOrders`            list page hook (uses the
 *                                          v_amazon_business_orders_with_match
 *                                          view so the list renders without
 *                                          fan-joining)
 *   - `useAmazonBusinessOrder(id)`         single-order detail with lines
 *   - `useImportAmazonBusinessCsv`         drives the IntegrationsPage
 *                                          uploader; base64-encodes the file
 *                                          and POSTs to the Edge Function
 *   - `useRerunAmazonBusinessMatch`        "Re-run matcher" button (Edge Fn)
 *   - `useUpdateAmazonBusinessLineQbAccount` per-line GL category change
 *                                          from the order-detail drawer
 *   - `useLinkAmazonBusinessOrder`         resolve a `review_required` order
 *                                          by pinning it to a chosen
 *                                          transaction (calls the RPC)
 *   - `useSetAmazonBusinessAppCredentials` Reports-API app-credentials setter
 *                                          (Edge Fn) for the scheduled path
 *
 * All of these surface super_admin-only because Amazon Business orders are
 * financial data; the IntegrationsPage and the /financials/amazon-business
 * page are both already MFA-gated at the route layer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { isSuperAdmin } from '@/lib/permissions';
import type {
  AmazonBusinessMatchStatus,
  Database,
  Tables,
} from '@/types/database';

const ORDERS_KEY = ['amazon-business-orders'];

type AmazonBusinessOrderListRow =
  Database['public']['Views']['v_amazon_business_orders_with_match']['Row'];

/**
 * Lists every Amazon Business order in the org, joined to line stats +
 * match status + matched-transaction details via the
 * v_amazon_business_orders_with_match view.
 *
 * Default sort: newest-first by order_date so the operator sees what
 * they just uploaded at the top.
 */
export function useAmazonBusinessOrders(opts: {
  limit?: number;
  status?: AmazonBusinessMatchStatus | 'all';
} = {}) {
  const { role, loading } = useAuth();
  const enabled = !loading && (isSuperAdmin(role) || role === 'admin' || role === 'manager');
  const limit = opts.limit ?? 500;
  const status = opts.status ?? 'all';
  return useQuery({
    queryKey: [...ORDERS_KEY, status, limit],
    enabled,
    queryFn: async () => {
      let query = supabase
        .from('v_amazon_business_orders_with_match')
        .select('*')
        .order('order_date', { ascending: false })
        .limit(limit);
      if (status !== 'all') query = query.eq('match_status', status);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as AmazonBusinessOrderListRow[];
    },
  });
}

/**
 * Single-order detail: header + lines (joined to chart_of_accounts so the
 * per-line category select can render its current value without an extra
 * lookup). Caller passes the `amazon_business_orders.id` UUID.
 */
export function useAmazonBusinessOrder(orderId: string | null) {
  const { role, loading } = useAuth();
  const enabled =
    !!orderId && !loading && (isSuperAdmin(role) || role === 'admin' || role === 'manager');
  return useQuery({
    queryKey: [...ORDERS_KEY, 'detail', orderId],
    enabled,
    queryFn: async () => {
      if (!orderId) return null;
      const { data: header, error: hErr } = await supabase
        .from('v_amazon_business_orders_with_match')
        .select('*')
        .eq('id', orderId)
        .single();
      if (hErr) throw hErr;
      const { data: lines, error: lErr } = await supabase
        .from('amazon_business_order_lines')
        .select(
          `*, chart_of_accounts:qb_account_id(id, name, code, account_type),
           items:item_id(id, name, sku)`,
        )
        .eq('amazon_business_order_id', orderId)
        .order('line_number', { ascending: true });
      if (lErr) throw lErr;
      // Cast via `unknown` because the hand-stubbed Supabase types in
      // src/types/database.ts don't yet declare the
      // amazon_business_order_lines.qb_account_id -> chart_of_accounts FK
      // (Relationships: []) and don't include the `items` table at all,
      // which makes the embedded `select` above unresolvable for the
      // PostgREST type inferrer. Once `supabase gen types typescript` is
      // run for real, this cast can be removed.
      return {
        header: header as AmazonBusinessOrderListRow,
        lines: ((lines ?? []) as unknown) as Array<
          Tables<'amazon_business_order_lines'> & {
            chart_of_accounts:
              | Pick<
                  Tables<'chart_of_accounts'>,
                  'id' | 'name' | 'code' | 'account_type'
                >
              | null;
            items: { id: string; name: string; sku: string | null } | null;
          }
        >,
      };
    },
  });
}

interface ImportResult {
  ok: boolean;
  imported_orders: number;
  imported_lines: number;
  skipped_lines: number;
  match: {
    total_processed: number;
    auto_matched: number;
    review_required: number;
    no_charge_yet: number;
    unchanged: number;
  } | null;
  unmatched_headers: string[];
}

/**
 * Reads an Amazon Business Order History Report CSV (from a <input
 * type="file">), base64-encodes it (so the Edge Function's JSON body
 * stays binary-safe even for non-ASCII addresses), and POSTs to
 * `amazon-business-import-csv`.
 */
export function useImportAmazonBusinessCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<ImportResult> => {
      const buf = await file.arrayBuffer();
      const csv_base64 = arrayBufferToBase64(buf);
      return invokeEdgeFunction<ImportResult>('amazon-business-import-csv', {
        csv_base64,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDERS_KEY });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

interface MatchResult {
  ok: boolean;
  summary: {
    total_processed: number;
    auto_matched: number;
    review_required: number;
    no_charge_yet: number;
    unchanged: number;
  };
}

/**
 * "Re-run matcher" button. Useful after a Plaid sync brings in a few new
 * charges that should now claim previously-unmatched orders.
 */
export function useRerunAmazonBusinessMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<MatchResult> => {
      return invokeEdgeFunction<MatchResult>('amazon-business-match', {});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDERS_KEY });
      qc.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Per-line GL category change from the order detail drawer. Writes
 * `amazon_business_order_lines.qb_account_id` so the next QBO push
 * builds the Purchase with the new split.
 */
export function useUpdateAmazonBusinessLineQbAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { line_id: string; qb_account_id: string | null }) => {
      const { error } = await supabase
        .from('amazon_business_order_lines')
        .update({ qb_account_id: input.qb_account_id })
        .eq('id', input.line_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDERS_KEY });
    },
  });
}

/**
 * Resolves a `review_required` order by pinning a specific Plaid charge.
 * Calls the SECURITY DEFINER `link_amazon_business_order` RPC (super_admin
 * only) which sets status='manual_matched' so the auto-matcher never
 * overrides the operator's pick.
 */
export function useLinkAmazonBusinessOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { order_id: string; transaction_id: string }) => {
      const { error } = await supabase.rpc('link_amazon_business_order', {
        p_order_id: input.order_id,
        p_transaction_id: input.transaction_id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDERS_KEY });
      qc.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Stores the org's Amazon Business Reporting API app credentials. Used
 * to light up the scheduled-pull path once Amazon approves the app —
 * not required to use the CSV upload path.
 */
export function useSetAmazonBusinessAppCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      application_id: string;
      client_id: string;
      client_secret: string;
    }) => {
      return invokeEdgeFunction<{ ok: boolean }>(
        'amazon-business-set-app-credentials',
        {
          application_id: input.application_id,
          client_id: input.client_id,
          client_secret: input.client_secret,
        },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });
}

// ---------------- shared helpers ----------------

async function invokeEdgeFunction<T>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    const message = await extractFunctionErrorMessage(error);
    throw new Error(message);
  }
  return data as T;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as FunctionsHttpError).context as Response | undefined;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  // Chunk to avoid blowing the call-stack on large CSVs (~10MB safe ceiling).
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}
